import React, { useEffect, useState } from 'react';
import { Code, Cpu, Globe, Zap, Cloud, Shield } from 'lucide-react';
import NavBar from "../components/NavBar";


// Expanded "Our Expertise" services – 6 items
const services = [
  {
    icon: Globe,
    title: "Full-Stack Development",
    description: "Building scalable web applications from the ground up with modern frameworks and robust architectures."
  },
  {
    icon: Cpu,
    title: "AI & Machine Learning Integration",
    description: "Incorporating advanced AI capabilities to empower your applications with predictive analytics and smart automation."
  },
  {
    icon: Code,
    title: "Custom Software Solutions",
    description: "Tailor-made development solutions addressing your unique business challenges with precision and innovation."
  },
  {
    icon: Zap,
    title: "Performance Optimization",
    description: "Enhancing speed and efficiency through code-level refinements and infrastructure tuning for a seamless user experience."
  },
  {
    icon: Cloud,
    title: "Cloud Infrastructure & DevOps",
    description: "Leveraging cloud-native tools and DevOps practices to ensure reliable, scalable, and continuously integrated solutions."
  },
  {
    icon: Shield,
    title: "Cybersecurity Solutions",
    description: "Implementing robust security measures to safeguard your digital assets and ensure data integrity and privacy."
  },
];

// New array for the Featured Projects section (general project info without company names)
const featuredProjects = [
  {
    title: "Education Platform",
    description: "A groundbreaking online education platform that revolutionizes learning with interactive features and personalized experiences."
  },
  {
    title: "Freelance Marketplace",
    description: "An innovative platform connecting talented professionals with global opportunities, offering seamless project management and secure transactions."
  },
  {
    title: "Restaurant Management System",
    description: "A comprehensive solution for the hospitality industry, streamlining operations from reservations to inventory management."
  },
  {
    title: "Healthcare Portal",
    description: "A secure and user-friendly portal that enhances patient care through real-time data integration and telemedicine solutions."
  },
];

// Detailed, animated process steps
const processSteps = [
  {
    step: "Discovery & Strategy",
    description: "We begin by understanding your business through comprehensive research and goal-setting, ensuring every subsequent step is data-driven."
  },
  {
    step: "Design & Prototyping",
    description: "Our creative team crafts intuitive designs and interactive prototypes, iterating quickly using wireframes and mockups to perfect the user experience."
  },
  {
    step: "Development & Integration",
    description: "Our developers bring designs to life using agile methodologies, integrating third-party services and optimizing performance at every stage."
  },
  {
    step: "Testing & Quality Assurance",
    description: "We conduct rigorous QA with automated and manual testing to identify and resolve issues early, ensuring a smooth and reliable product."
  },
  {
    step: "Deployment & Support",
    description: "Post-deployment, we offer ongoing maintenance and monitoring, ensuring your solution evolves with your business needs."
  },
];

// Animated Matrix Background Component
const MatrixBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById('matrix-webdev-bg');
    const context = canvas.getContext('2d');

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const katakana = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    const latin = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nums = '0123456789';
    const alphabet = katakana + latin + nums;

    const fontSize = 16;
    const columns = width / fontSize;
    const rainDrops = new Array(Math.floor(columns)).fill(1);

    const draw = () => {
      context.fillStyle = 'rgba(0, 0, 0, 0.05)';
      context.fillRect(0, 0, width, height);
      context.fillStyle = '#0F0';
      context.font = fontSize + 'px monospace';

      for (let i = 0; i < rainDrops.length; i++) {
        const text = alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        context.fillText(text, i * fontSize, rainDrops[i] * fontSize);
        if (rainDrops[i] * fontSize > height && Math.random() > 0.975) {
          rainDrops[i] = 0;
        }
        rainDrops[i]++;
      }
    };

    const interval = setInterval(draw, 30);

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas 
      id="matrix-webdev-bg" 
      className="fixed top-0 left-0 w-full h-full opacity-20 pointer-events-none"
    />
  );
};

const WebDevSection = () => {
  const [visible, setVisible] = useState(false);

  // Reveal content with a smooth entrance animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
      <NavBar />
      <MatrixBackground />
      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Header Section */}
        <header className={`text-center mb-12 transition-all duration-700 ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <h1 className="text-5xl font-bold font-mono text-green-500 mb-4">
            Full-Stack & AI Web Development
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            We combine cutting-edge technologies with innovative design to build digital experiences that are powerful, engaging, and transformative. Discover our expertise, process, and featured projects.
          </p>
        </header>

        {/* Our Expertise Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-mono font-bold text-green-400 mb-8 text-center">Our Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className={`bg-gray-800/50 p-6 rounded-lg border border-green-500/20 hover:border-green-500/40 transition-transform duration-300 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon className="w-10 h-10 text-green-500" />
                    <h3 className="text-2xl font-mono font-bold">{service.title}</h3>
                  </div>
                  <p className="text-gray-300">{service.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Featured Projects Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-mono font-bold text-green-400 mb-8 text-center">Featured Projects</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <div 
                key={index} 
                className={`bg-gray-800/60 p-6 rounded-lg border border-green-500/20 hover:border-green-500/40 transition-all duration-300 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <h3 className="text-xl font-mono font-bold text-green-500 mb-2">{project.title}</h3>
                <p className="text-gray-300 text-sm">{project.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Our Process Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-mono font-bold text-green-400 mb-8 text-center">Our Process</h2>
          <div className={`max-w-4xl mx-auto relative transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="border-l-2 border-green-500 pl-6">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className={`relative mb-10 pl-4 transition-transform duration-500 ${visible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900"></div>
                  <h3 className="font-mono font-bold text-xl text-green-400">{step.step}</h3>
                  <p className="text-gray-300 mt-2">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call-to-Action Section */}
        <section className="text-center">
          <h2 className="text-3xl font-mono font-bold text-green-400 mb-4">Let's Build Something Amazing</h2>
          <p className="text-gray-300 mb-8">
            Ready to transform your digital presence? Contact us today and let's create innovative web solutions tailored to your needs.
          </p>
          <a 
            href="/contact"  // Ensure your routing handles this path correctly
            className="px-8 py-3 bg-green-600 hover:bg-green-500 text-white font-mono rounded-lg transition-colors duration-300"
          >
            Get in Touch
          </a>
        </section>
      </div>
    </section>
  );
};

export default WebDevSection;
